import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Loader2, TrendingUp, Users, ArrowRightLeft } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const numFmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));

// Grouped bar chart for inflow vs outflow
const InflowOutflowChart = ({ data, height = 220 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(
    ...data.flatMap((d) => [d.joined, d.notified]),
    3
  );
  const barW = 100 / (data.length * 2 + data.length); // spacing
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      className="w-full"
      style={{ height }}
      preserveAspectRatio="none"
      data-testid="waitlist-flow-chart"
    >
      <line x1="0" y1={height - 20} x2="100" y2={height - 20}
             stroke="#0F3D2E" strokeWidth="0.2" />
      {data.map((d, i) => {
        const groupW = 100 / data.length;
        const cx = groupW * i + groupW / 2;
        const w = groupW * 0.28;
        const joinedH = (d.joined / max) * (height - 30);
        const notifiedH = (d.notified / max) * (height - 30);
        return (
          <g key={d.week}>
            {/* Joined = gold */}
            <rect
              x={cx - w - 0.5}
              y={height - 20 - joinedH}
              width={w}
              height={joinedH}
              fill="#FFD700"
            />
            {/* Notified = navy */}
            <rect
              x={cx + 0.5}
              y={height - 20 - notifiedH}
              width={w}
              height={notifiedH}
              fill="#0F3D2E"
            />
            <text
              x={cx}
              y={height - 8}
              fontSize="3"
              fill="#666"
              textAnchor="middle"
              fontFamily="monospace"
            >
              {d.week.split("-W").pop()}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default function AdminWaitlistAnalytics() {
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [weeks, setWeeks] = useState(12);
  const [notifying, setNotifying] = useState(false);

  const load = () => {
    api
      .get(`/admin/founder-premium/waitlist/analytics?weeks=${weeks}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/admin/founder-premium/waitlist?include_notified=true&limit=200")
      .then((r) => setEntries(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  const notifyQuarter = async () => {
    if (!window.confirm("Notify every un-notified waitlist entry that a new quarter is open? Emails will fire.")) return;
    setNotifying(true);
    try {
      const { data } = await api.post("/admin/founder-premium/waitlist/notify-quarter");
      toast.success(`Notified ${data.notified} founders for ${data.quarter}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifying(false);
    }
  };

  if (!data) {
    return (
      <DashboardShell title="Waitlist Analytics" subtitle="ADMIN · PREMIUM DEMAND">
        <div className="text-ink-muted">
          <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
        </div>
      </DashboardShell>
    );
  }

  const totals = (data.timeseries || []).reduce(
    (acc, r) => ({
      joined: acc.joined + r.joined,
      notified: acc.notified + r.notified,
    }),
    { joined: 0, notified: 0 }
  );

  return (
    <DashboardShell title="Waitlist Analytics" subtitle="ADMIN · PREMIUM DEMAND">
      <Toaster richColors position="top-right" />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="overline">Range</span>
          {[8, 12, 26, 52].map((w) => (
            <button
              key={w}
              onClick={() => setWeeks(w)}
              data-testid={`waitlist-range-${w}`}
              className={
                "px-3 py-1 rounded text-xs font-mono uppercase tracking-wider " +
                (weeks === w
                  ? "bg-navy text-white"
                  : "bg-white text-ink border border-navy/[0.15]")
              }
            >
              {w}w
            </button>
          ))}
        </div>
        <button
          onClick={notifyQuarter}
          disabled={notifying}
          data-testid="waitlist-notify-quarter"
          className="px-3 py-2 rounded bg-gold text-navy text-xs font-heading uppercase tracking-wider hover:bg-gold/90 disabled:opacity-50"
        >
          {notifying ? <Loader2 className="animate-spin inline" size={12} /> : "Notify all — New quarter open"}
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-navy/[0.08] p-4 rounded-md bg-white" data-testid="wl-summary-queue">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-navy" />
            <div className="overline">Current queue</div>
          </div>
          <div className="font-heading text-3xl text-ink">{numFmt(data.current_queue)}</div>
          <div className="text-[10px] font-mono text-ink-muted mt-1">Waiting for a slot</div>
        </div>
        <div className="border border-navy/[0.08] p-4 rounded-md bg-white" data-testid="wl-summary-inflow">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-gold" />
            <div className="overline">Inflow · {weeks}w</div>
          </div>
          <div className="font-heading text-3xl text-ink">{numFmt(totals.joined)}</div>
          <div className="text-[10px] font-mono text-ink-muted mt-1">Founders who joined</div>
        </div>
        <div className="border border-navy/[0.08] p-4 rounded-md bg-white" data-testid="wl-summary-outflow">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRightLeft size={14} className="text-emerald-600" />
            <div className="overline">Outflow · {weeks}w</div>
          </div>
          <div className="font-heading text-3xl text-ink">{numFmt(totals.notified)}</div>
          <div className="text-[10px] font-mono text-ink-muted mt-1">Founders notified</div>
        </div>
        <div className="border border-navy/[0.08] p-4 rounded-md bg-white" data-testid="wl-summary-total">
          <div className="overline">Total ever</div>
          <div className="font-heading text-3xl text-ink">{numFmt(data.total_ever)}</div>
          <div className="text-[10px] font-mono text-ink-muted mt-1">Lifetime waitlist entries</div>
        </div>
      </div>

      {/* Chart */}
      <Card testId="waitlist-chart-card" className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Weekly Inflow vs Outflow</div>
            <div className="text-xs text-ink-muted mt-0.5">
              <span className="inline-block w-3 h-3 bg-gold mr-1 rounded-sm align-middle" />
              Joined
              <span className="inline-block w-3 h-3 bg-navy ml-3 mr-1 rounded-sm align-middle" />
              Notified
            </div>
          </div>
        </div>
        <InflowOutflowChart data={data.timeseries || []} />
      </Card>

      {/* Reason breakdown */}
      {Object.keys(data.notified_by_reason || {}).length > 0 && (
        <Card testId="waitlist-reasons-card" className="mb-6">
          <div className="overline mb-3">Notification reasons — lifetime</div>
          <div className="space-y-2">
            {Object.entries(data.notified_by_reason).map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between border border-navy/[0.06] rounded p-3"
                data-testid={`wl-reason-${k}`}
              >
                <div className="text-sm text-ink">{k.replace(/_/g, " ")}</div>
                <Pill tone="gold">{v}× notified</Pill>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Raw entries */}
      <Card testId="waitlist-entries-card">
        <div className="overline mb-3">Recent Waitlist Entries · {entries.length}</div>
        {entries.length === 0 ? (
          <div className="text-ink-muted italic text-sm">No entries yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                <th className="text-left py-2">Joined</th>
                <th className="text-left py-2">Startup</th>
                <th className="text-left py-2">Email</th>
                <th className="text-right py-2">Status</th>
                <th className="text-right py-2 pr-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 40).map((e) => (
                <tr key={e.waitlist_id} className="border-t border-navy/[0.06]">
                  <td className="py-2 font-mono text-[11px] text-ink-muted">
                    {e.joined_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-2">{e.startup_name || e.startup_id}</td>
                  <td className="py-2 text-xs text-ink-muted">{e.email || "—"}</td>
                  <td className="py-2 text-right">
                    <Pill tone={e.notified_at ? "success" : "gold"}>
                      {e.notified_at ? "Notified" : "Waiting"}
                    </Pill>
                  </td>
                  <td className="py-2 pr-2 text-right text-[10px] font-mono text-ink-muted">
                    {e.notified_reason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </DashboardShell>
  );
}
